import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import { useEffect } from 'react'

// Layouts
import AppLayout from './components/layouts/AppLayout'
import AuthLayout from './components/layouts/AuthLayout'

// Pages
import Dashboard from './pages/Dashboard'
import Projects from './pages/Projects'
import ProjectDetail from './pages/ProjectDetail'
import Correspondances from './pages/Correspondances'
import Users from './pages/Users'
import Settings from './pages/Settings'
import ImportExport from './pages/ImportExport'
import Login2 from './pages/Login2'
import Register2 from './pages/Register2'
import VerifyEmail from './pages/VerifyEmail'
import RncpVisualization from './pages/RncpVisualization'
import RncpMain from './pages/RncpMain'
import ListingFormation from './pages/ListingFormation'
import RncpSearch from './pages/RncpSearch'
import RncpMapping from './pages/RncpMapping'
import RncpTest from './pages/RncpTest'
import Audit from './pages/Audit'
import FranceTravailTest from './pages/FranceTravailTest'
import RomeCompetencesTest from './pages/RomeCompetencesTest'
import QuiFormeTest from './pages/QuiFormeTest'
import IntercariforefTest from './pages/IntercariforefTest'
import FusionTest from './pages/FusionTest'
import OnboardingTest from './pages/OnboardingTest'

// New Module Pages
import Metier from './pages/Metier'
import Chaine from './pages/Chaine'
import Navette from './pages/Navette'
import Trame from './pages/Trame'
import Aiguille from './pages/Aiguille'
import Tapis from './pages/Tapis'
import Echeveau from './pages/Echeveau'
import Fusette from './pages/Fusette'
import Ourdissage from './pages/Ourdissage'
import PrivacyPolicy from './pages/PrivacyPolicy'
import ApiTest from './pages/ApiTest'
import OrbloomPresentation from './pages/OrbloomPresentation'
import TestApiFranceTravail from './pages/TestApiFranceTravail'
import TestScraping from './pages/TestScraping'
import TestFusionOnboarding from './pages/TestFusionOnboarding'
import Onboarding2 from './pages/Onboarding2'
import Onboarding3 from './pages/Onboarding3'

// Freemium Pages
import OrganizationStep from './pages/Onboarding/OrganizationStep'
import FormationsStep from './pages/Onboarding/FormationsStep'
import PricingPage from './pages/PricingPage'
import Pricing from './pages/Pricing'
import AnalysisPage from './pages/AnalysisPage'

// Support Pages
import KnowledgeBase from './pages/KnowledgeBase'
import Contact from './pages/Contact'

// Public Pages
import LandingPage from './pages/LandingPage'

// Composants
import PrivateRoute from './components/PrivateRoute'
import LoadingSpinner from './components/LoadingSpinner'
import ConsentBanner from './components/ConsentBanner'
import PWAUpdatePrompt from './components/PWA/PWAUpdatePrompt'

// Services
// import analytics from './utils/analytics' // ⚠️ DÉSACTIVÉ - Google Analytics supprimé
import { captureEvent, identifyUser, resetUser } from './utils/posthog'

function App() {
  const { user, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // ⚠️ DÉSACTIVÉ - Google Analytics supprimé (on garde uniquement PostHog)
  // useEffect(() => {
  //   analytics.init()
  // }, [])

  // ⚠️ DÉSACTIVÉ TEMPORAIREMENT - Redirection automatique vers onboarding
  // Redirection automatique vers onboarding si non complété
  // useEffect(() => {
  //   if (user && !isLoading) {
  //     const isOnboardingRoute = location.pathname.startsWith('/onboarding')
  //     const isAuthRoute = ['/login', '/register'].includes(location.pathname)

  //     // Si l'utilisateur n'a pas terminé l'onboarding et n'est pas déjà sur une route onboarding/auth
  //     if (!user.onboardingCompleted && !isOnboardingRoute && !isAuthRoute) {
  //       console.log('🔄 Redirection vers onboarding - utilisateur non complété')
  //       navigate('/onboarding/organization', { replace: true })
  //     }
  //   }
  // }, [user, isLoading, location.pathname, navigate])

  // Tracker les changements de page (PostHog uniquement)
  useEffect(() => {
    const pagePath = location.pathname
    const pageTitle = location.pathname.split('/').filter(Boolean).join(' > ') || 'Accueil'

    // PostHog - Page view (automatique via autocapture, mais on peut aussi tracker manuellement)
    captureEvent('$pageview', {
      page_path: pagePath,
      page_title: pageTitle
    })
  }, [location])

  // Définir l'utilisateur connecté pour le tracking (PostHog uniquement)
  useEffect(() => {
    if (user) {
      // PostHog - Identifier l'utilisateur (avec consentement)
      if (user.analyticsConsent) {
        identifyUser(user.id, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          plan: user.subscriptionPlan,
          onboardingCompleted: user.onboardingCompleted
        })
      } else {
        // Sans consentement, on identifie quand même mais sans email
        identifyUser(user.id, {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          plan: user.subscriptionPlan,
          onboardingCompleted: user.onboardingCompleted
        })
      }
    } else {
      // Déconnexion
      resetUser()
    }
  }, [user])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <SocketProvider>
    <Routes>
      {/* Routes publiques - Landing Page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/landing" element={<LandingPage />} />

      {/* Routes publiques - Auth */}
      <Route path="/login" element={<Login2 />} />
      <Route path="/register" element={<Register2 />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/orbloom" element={<OrbloomPresentation />} />

      {/* Routes publiques - Support (accessibles sans login) */}
      <Route path="/knowledge-base" element={<KnowledgeBase />} />
      <Route path="/contact" element={<Contact />} />


      {/* Routes privées - Application */}
      <Route path="/dashboard" element={
        <PrivateRoute>
          <AppLayout />
        </PrivateRoute>
      }>
        <Route index element={<Dashboard />} />
      </Route>

      {/* Routes privées - Projets */}
      <Route path="/projects" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Projects />} />
        <Route path=":projectId" element={<ProjectDetail />} />
      </Route>

      {/* Routes privées - Correspondances */}
      <Route path="/correspondances/:projectId" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Correspondances />} />
      </Route>

      {/* Routes privées - Modules Orbloom */}
      <Route path="/metier" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Metier />} />
      </Route>
      <Route path="/chaine" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Chaine />} />
      </Route>
      <Route path="/navette" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Navette />} />
      </Route>
      <Route path="/trame" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Trame />} />
      </Route>
      <Route path="/aiguille" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Aiguille />} />
      </Route>
      <Route path="/tapis" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Tapis />} />
      </Route>
      <Route path="/echeveau" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Echeveau />} />
      </Route>
      <Route path="/fusette" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Fusette />} />
      </Route>
      <Route path="/ourdissage" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Ourdissage />} />
      </Route>

      {/* Routes privées - RNCP */}
      <Route path="/rncp-visualization" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<RncpVisualization />} />
      </Route>
      <Route path="/rncp" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<RncpMain />} />
      </Route>
      <Route path="/listing-formation" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<ListingFormation />} />
      </Route>
      <Route path="/rncp-search" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<RncpSearch />} />
      </Route>
      <Route path="/rncp-mapping" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<RncpMapping />} />
      </Route>
      <Route path="/rncp-test" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<RncpTest />} />
      </Route>

      {/* Routes privées - Import/Export */}
      <Route path="/import-export" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<ImportExport />} />
      </Route>

      {/* Routes privées - Utilisateurs */}
      <Route path="/users" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Users />} />
      </Route>

      {/* Routes privées - Audit */}
      <Route path="/audit" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Audit />} />
      </Route>

      {/* Routes privées - Settings */}
      <Route path="/settings" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<Settings />} />
      </Route>

      {/* Routes privées - Tests */}
      <Route path="/france-travail-test" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<FranceTravailTest />} />
      </Route>
      <Route path="/rome-competences-test" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<RomeCompetencesTest />} />
      </Route>
      <Route path="/quiforme-test" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<QuiFormeTest />} />
      </Route>
      <Route path="/intercariforef-test" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<IntercariforefTest />} />
      </Route>
      <Route path="/fusion-test" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<FusionTest />} />
      </Route>
      <Route path="/onboarding-test" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<OnboardingTest />} />
      </Route>
      <Route path="/tests-api" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<ApiTest />} />
      </Route>
      <Route path="/test-api-france-travail" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<TestApiFranceTravail />} />
      </Route>
      <Route path="/test-scraping" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<TestScraping />} />
      </Route>
      <Route path="/test-fusion-onboarding" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<TestFusionOnboarding />} />
      </Route>

      {/* Routes privées - Freemium */}
      <Route path="/onboarding2" element={<Onboarding2 />} />
      <Route path="/onboarding3" element={<Onboarding3 />} />
      <Route path="/analysis" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<AnalysisPage />} />
      </Route>

      {/* Route Pricing publique */}
      <Route path="/pricing" element={<Pricing />} />

      {/* Onboarding Routes (hors AppLayout) - ⚠️ DÉSACTIVÉ TEMPORAIREMENT */}
      {/* <Route path="/onboarding/organization" element={
        <PrivateRoute>
          <OrganizationStep />
        </PrivateRoute>
      } />
      <Route path="/onboarding/formations" element={
        <PrivateRoute>
          <FormationsStep />
        </PrivateRoute>
      } /> */}

      {/* Route 404 */}
      <Route path="*" element={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
            <p className="text-gray-600">Page non trouvée</p>
          </div>
        </div>
      } />
    </Routes>

    {/* Bandeau de consentement cookies - PostHog */}
    <ConsentBanner />

    {/* PWA Update Prompt */}
    <PWAUpdatePrompt />
  </SocketProvider>
  )
}

export default App